import moment from "moment";
import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Badge,
  Button,
  Label,
  Modal,
  ModalBody,
  ModalHeader,
  Spinner,
  Textarea,
  Timeline,
} from "flowbite-react";
import { useQuery } from "react-query";
import axios from "../../config/axiosInstance";
import { AUDIT } from "../../config/apiConfig";
import { formatToReal } from "../../utils/utils";
import { buildPaymentAuditPayload } from "../../utils/paymentAudit";
import { MdOutlineComment } from "react-icons/md";

const formatDateTime = (value) =>
  value ? moment(value).format("DD/MM/YYYY - HH:mm") : "—";

const isOverdue = (payment) =>
  !payment?.isPaid &&
  payment?.dueDate &&
  moment(payment.dueDate).startOf("day").isBefore(moment().startOf("day"));

const normalizeRows = (response) => {
  if (Array.isArray(response)) return response;
  if (Array.isArray(response?.data)) return response.data;
  if (Array.isArray(response?.data?.data)) return response.data.data;
  if (Array.isArray(response?.rows)) return response.rows;
  return [];
};

const isPaymentField = (field) =>
  ["dueDate", "isPaid", "price", "paymentLink", "description", "comment"].includes(
    field
  );

const isCommentEvent = (event, fields) =>
  event?.action === "COMMENT" ||
  event?.source === "payment_comment" ||
  fields.includes("comment") ||
  Boolean(event?.newValues?.comment);

const renderValue = (field, value) => {
  if (value == null || value === "") return "—";
  if (field === "dueDate") return moment(value).format("DD/MM/YYYY");
  if (field === "isPaid") return value ? "Pago" : "Pendente";
  if (field === "price" || field === "value" || field === "amount")
    return formatToReal(value);
  return String(value);
};

const actionMeta = (event) => {
  const fields = Array.isArray(event?.changedFields)
    ? event.changedFields
    : [];
  if (isCommentEvent(event, fields)) {
    return { label: "Comentário", color: "gray" };
  }
  if (event?.action === "DELETE") {
    return { label: "Excluída", color: "failure" };
  }
  if (event?.action === "CREATE") {
    return { label: "Criada", color: "success" };
  }
  if (fields.includes("dueDate")) {
    return { label: "Vencimento alterado", color: "warning" };
  }
  if (fields.includes("isPaid")) {
    return { label: "Pagamento confirmado", color: "success" };
  }
  if (fields.some(isPaymentField)) {
    return { label: "Atualizada", color: "info" };
  }
  if (event?.synthetic === "EXPIRED") {
    return { label: "Vencida", color: "failure" };
  }
  return { label: event?.action || "Evento", color: "gray" };
};

const paymentLabel = (payment) =>
  payment?.Company?.name ||
  payment?.Company?.socialReason ||
  payment?.companyName ||
  payment?.name ||
  "Cobrança";

const PaymentHistoryModal = ({
  open,
  onClose,
  payment,
  companyId,
  allowComment = false,
  source = "admin_panel",
}) => {
  const resolvedCompanyId =
    companyId ?? payment?.companyId ?? payment?.Company?.id ?? null;
  const [comment, setComment] = useState("");
  const [commentError, setCommentError] = useState("");
  const [commentLoading, setCommentLoading] = useState(false);

  const {
    data: auditResponse,
    isFetching,
    isError,
    refetch,
  } = useQuery(
    ["payment-history", resolvedCompanyId, payment?.id],
    async () => {
      const { data } = await axios.get(
        `${AUDIT.LIST_BY_COMPANY(resolvedCompanyId)}`,
        {
          params: {
            page: 1,
            pageSize: 100,
          },
        }
      );
      return data;
    },
    { enabled: open && !!resolvedCompanyId }
  );

  useEffect(() => {
    if (!open) {
      setComment("");
      setCommentError("");
      setCommentLoading(false);
    }
  }, [open]);

  const auditRows = useMemo(() => {
    const rows = normalizeRows(auditResponse);
    const paymentId = String(payment?.id ?? "");
    return rows.filter((row) => {
      const rowEntityId = String(row?.entityId ?? "");
      const rowEntityName = String(row?.entityName ?? "").toLowerCase();
      if (!paymentId) return false;
      if (rowEntityId && rowEntityId !== "undefined" && rowEntityId !== "null") {
        return rowEntityId === paymentId;
      }
      return (
        rowEntityName.includes("payment") ||
        rowEntityName.includes("invoice") ||
        rowEntityName.includes("boleto")
      );
    });
  }, [auditResponse, payment?.id]);

  const timelineEvents = useMemo(() => {
    const events = [];
    const hasCreateAudit = auditRows.some((row) => row?.action === "CREATE");

    if (payment?.createdAt && !hasCreateAudit) {
      events.push({
        id: `created-${payment.id}`,
        when: payment.createdAt,
        action: "CREATE",
        title: "Cobrança criada",
        reason: `Valor ${formatToReal(payment?.price)} • Vencimento ${
          payment?.dueDate ? moment(payment.dueDate).format("DD/MM/YYYY") : "—"
        }`,
      });
    }

    auditRows.forEach((row, index) => {
      const fields = Array.isArray(row?.changedFields) ? row.changedFields : [];
      const commentEvent = isCommentEvent(row, fields);
      let title = "Atualização";
      if (commentEvent) title = "Comentário";
      else if (row?.action === "DELETE") title = "Cobrança excluída";
      else if (fields.includes("dueDate")) title = "Vencimento alterado";
      else if (fields.includes("isPaid")) title = "Pagamento confirmado";
      else if (row?.action === "CREATE") title = "Cobrança criada";

      events.push({
        id: row?.id || `audit-${index}`,
        when: row?.createdAt,
        action: row?.action,
        title,
        reason: row?.newValues?.comment || row?.reason || row?.comment || "",
        fields,
        previousValues: row?.previousValues || {},
        newValues: row?.newValues || {},
      });
    });

    if (isOverdue(payment)) {
      events.push({
        id: `expired-${payment.id}`,
        when: payment?.dueDate || payment?.updatedAt || new Date().toISOString(),
        action: "EXPIRED",
        title: "Cobrança vencida",
        reason: "A cobrança está atualmente vencida e sem baixa registrada.",
        synthetic: "EXPIRED",
      });
    }

    return events.sort((a, b) => new Date(a.when) - new Date(b.when));
  }, [auditRows, payment]);

  const handleAddComment = async () => {
    const text = comment.trim();
    if (!text || !payment?.id || !resolvedCompanyId) return;

    try {
      setCommentLoading(true);
      setCommentError("");
      const payload = buildPaymentAuditPayload({
        action: "UPDATE",
        payment,
        companyId: resolvedCompanyId,
        source,
        changedFields: ["comment"],
        newValues: { comment: text },
      });

      if (!payload) throw new Error("Payload inválido");

      await axios.post(AUDIT.CREATE, payload);
      setComment("");
      await refetch();
    } catch (error) {
      setCommentError(
        error?.response?.data?.message ||
          "Não foi possível salvar o comentário."
      );
    } finally {
      setCommentLoading(false);
    }
  };

  const canComment = allowComment && !!payment?.id && !!resolvedCompanyId;
  const currentStatus = payment?.isPaid
    ? "Pago"
    : isOverdue(payment)
    ? "Vencida"
    : "Aberta";

  return (
    <Modal show={open} onClose={onClose} size="5xl" popup>
      <ModalHeader />
      <ModalBody>
        <div className="space-y-5">
          <div className="flex flex-col gap-2">
            <div className="text-xl font-semibold text-slate-900">
              Histórico do pagamento
            </div>
            <div className="text-sm text-slate-500">
              {paymentLabel(payment)}
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
            <div className="rounded-lg border border-slate-200 bg-slate-50 p-3">
              <div className="text-xs uppercase tracking-wide text-slate-500">
                Criado em
              </div>
              <div className="mt-1 text-sm font-medium text-slate-900">
                {formatDateTime(payment?.createdAt)}
              </div>
            </div>
            <div className="rounded-lg border border-slate-200 bg-slate-50 p-3">
              <div className="text-xs uppercase tracking-wide text-slate-500">
                Vencimento
              </div>
              <div className="mt-1 text-sm font-medium text-slate-900">
                {payment?.dueDate ? moment(payment.dueDate).format("DD/MM/YYYY") : "—"}
              </div>
            </div>
            <div className="rounded-lg border border-slate-200 bg-slate-50 p-3">
              <div className="text-xs uppercase tracking-wide text-slate-500">
                Valor
              </div>
              <div className="mt-1 text-sm font-medium text-slate-900">
                {formatToReal(payment?.price)}
              </div>
            </div>
            <div className="rounded-lg border border-slate-200 bg-slate-50 p-3">
              <div className="text-xs uppercase tracking-wide text-slate-500">
                Status atual
              </div>
              <div className="mt-2">
                <Badge
                  color={
                    payment?.isPaid
                      ? "success"
                      : isOverdue(payment)
                      ? "failure"
                      : "warning"
                  }
                  className="w-fit"
                >
                  {currentStatus}
                </Badge>
              </div>
            </div>
          </div>

          {canComment && (
            <div className="rounded-lg border border-slate-200 bg-white p-4">
              <div className="mb-2 flex items-center gap-2 text-sm font-semibold text-slate-900">
                <MdOutlineComment />
                Comentário
              </div>
              <Label className="mb-1 block text-xs text-slate-500">
                Adicione uma observação para este pagamento
              </Label>
              <Textarea
                rows={3}
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                placeholder="Ex.: cliente avisou que vai pagar na próxima semana"
              />
              {commentError && (
                <Alert className="mt-3" color="failure" withBorderAccent>
                  {commentError}
                </Alert>
              )}
              <div className="mt-3 flex justify-end gap-2">
                <Button
                  color="gray"
                  onClick={() => setComment("")}
                  disabled={commentLoading || !comment}
                >
                  Limpar
                </Button>
                <Button
                  color="success"
                  onClick={handleAddComment}
                  disabled={commentLoading || !comment.trim()}
                  isProcessing={commentLoading}
                  processingSpinner={<Spinner size="sm" />}
                >
                  Salvar comentário
                </Button>
              </div>
            </div>
          )}

          {isFetching ? (
            <div className="flex items-center justify-center py-10">
              <Spinner size="xl" />
            </div>
          ) : isError ? (
            <Alert color="failure" withBorderAccent>
              Não foi possível carregar o histórico deste pagamento.
            </Alert>
          ) : timelineEvents.length > 0 ? (
            <Timeline>
              {timelineEvents.map((event) => {
                const meta = actionMeta(event);
                const fields = Array.isArray(event?.fields) ? event.fields : [];
                return (
                  <Timeline.Item key={event.id}>
                    <Timeline.Point />
                    <Timeline.Content>
                      <Timeline.Time>{formatDateTime(event.when)}</Timeline.Time>
                      <div className="flex items-start justify-between gap-3">
                        <Timeline.Title className="text-base">
                          {event.title}
                        </Timeline.Title>
                        <Badge color={meta.color} className="shrink-0">
                          {meta.label}
                        </Badge>
                      </div>
                      <Timeline.Body className="space-y-2 text-sm">
                        {event.reason && (
                          <div className="text-slate-700">{event.reason}</div>
                        )}

                        {fields.length > 0 && (
                          <div className="flex flex-wrap gap-1">
                            {fields.map((field) => (
                              <Badge key={field} color="gray" size="xs">
                                {field}
                              </Badge>
                            ))}
                          </div>
                        )}

                        {fields.length > 0 && (
                          <div className="rounded-md border border-slate-200 bg-slate-50 p-3 text-xs text-slate-700">
                            {fields.map((field) => (
                              <div key={field} className="mb-1 last:mb-0">
                                <span className="font-medium">{field}: </span>
                                <span className="line-through opacity-70 mr-1">
                                  {renderValue(field, event.previousValues?.[field])}
                                </span>
                                <span>→ {renderValue(field, event.newValues?.[field])}</span>
                              </div>
                            ))}
                          </div>
                        )}
                      </Timeline.Body>
                    </Timeline.Content>
                  </Timeline.Item>
                );
              })}
            </Timeline>
          ) : (
            <Alert color="gray" withBorderAccent>
              Nenhum evento encontrado para este pagamento.
            </Alert>
          )}
        </div>
      </ModalBody>
    </Modal>
  );
};

export default PaymentHistoryModal;
